import React, { memo, useEffect, useState } from "react";
import { ContactEditFormValues, ContactEmailRow, ContactPhoneRow, ContactAddressRow, ContactNoteRow } from "services/api/contacts/types";
import { getContactEmailsAPI, getContactPhonesAPI, getContactAddressesAPI, getContactNotesAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { ReactSVG } from "react-svg";
import Badge from "ui/components/Badge/Badge";

interface ContactSummaryStepProps {
  formValues: ContactEditFormValues;
}

const ContactSummaryStep: React.FC<ContactSummaryStepProps> = ({ formValues }) => {
  const contactId = formValues.id;
  const [emails, setEmails] = useState<ContactEmailRow[]>([]);
  const [phones, setPhones] = useState<ContactPhoneRow[]>([]);
  const [addresses, setAddresses] = useState<ContactAddressRow[]>([]);
  const [notes, setNotes] = useState<ContactNoteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (contactId) fetchAll();
  }, [contactId]);

  const fetchAll = async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const [emailRes, phoneRes, addressRes, noteRes] = await Promise.all([
        getContactEmailsAPI(initialDataWithPagination.pagination, contactId),
        getContactPhonesAPI(initialDataWithPagination.pagination, contactId),
        getContactAddressesAPI(initialDataWithPagination.pagination, contactId),
        getContactNotesAPI(initialDataWithPagination.pagination, contactId),
      ]);
      setEmails(emailRes.data);
      setPhones(phoneRes.data);
      setAddresses(addressRes.data);
      setNotes(noteRes.data);
    } catch {
    }
    setIsLoading(false);
  };

  const isCompany = formValues.contactTypeId === "C";
  const name = isCompany
    ? formValues.companyName
    : `${formValues.firstName ?? ""} ${formValues.lastName ?? ""}`.trim();

  const pictures = formValues.pictures ?? [];
  const totalFiles = pictures.reduce((acc, p) => acc + p.files.length, 0);

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Pregled kontakta</h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Provjerite unesene podatke prije objave kontakta.
          Klikom na <strong>"Spremi"</strong> kontakt će biti objavljen i vidljiv u sustavu.
        </p>

        {isLoading ? (
          <Loader isLoading={true}> </Loader>
        ) : (
          <div className="border border-slate-200">
            {/* Osnovni podaci */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/contact.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Osnovni podaci</h3>
                  <p className="text-xs text-slate-500">Pregled osnovnih informacija o kontaktu</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 text-sm">
                <div>
                  <span className="text-slate-400">Naziv:</span>
                  <span className="ml-2 text-slate-700 font-medium">{name || "-"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Tip:</span>
                  <span className="ml-2 text-slate-700 font-medium">{isCompany ? "Tvrtka" : "Osoba"}</span>
                </div>
                <div>
                  <span className="text-slate-400">OIB:</span>
                  <span className="ml-2 text-slate-700 font-medium">{formValues.personalIdentificationNumber || "-"}</span>
                </div>
                {!isCompany && formValues.jobTitle && (
                  <div>
                    <span className="text-slate-400">Funkcija:</span>
                    <span className="ml-2 text-slate-700 font-medium">{formValues.jobTitle}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/email.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Email adrese <span className="text-xs text-slate-400 font-normal">({emails.length})</span></h3>
                  <p className="text-xs text-slate-500">Unesene email adrese kontakta</p>
                </div>
              </div>
              {emails.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih email adresa</p>
              ) : (
                <div className="space-y-2">
                  {emails.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{e.emailAddress}</span>
                      {e.primary && <Badge text="Primarni" variant="info" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Telefon */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/phone.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Telefoni <span className="text-xs text-slate-400 font-normal">({phones.length})</span></h3>
                  <p className="text-xs text-slate-500">Uneseni telefonski brojevi kontakta</p>
                </div>
              </div>
              {phones.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih telefona</p>
              ) : (
                <div className="space-y-2">
                  {phones.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{p.phoneNumber}</span>
                      {p.primary && <Badge text="Primarni" variant="info" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Adrese */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/location.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Adrese <span className="text-xs text-slate-400 font-normal">({addresses.length})</span></h3>
                  <p className="text-xs text-slate-500">Unesene adrese kontakta</p>
                </div>
              </div>
              {addresses.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih adresa</p>
              ) : (
                <div className="space-y-2">
                  {addresses.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{a.line1}{a.city ? `, ${a.postalCode} ${a.city}` : ""}</span>
                      {a.primary && <Badge text="Primarna" variant="info" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bilješke */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/note.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Bilješke <span className="text-xs text-slate-400 font-normal">({notes.length})</span></h3>
                  <p className="text-xs text-slate-500">Unesene bilješke kontakta</p>
                </div>
              </div>
              {notes.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih bilješki</p>
              ) : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <div key={n.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="truncate">{n.content}</span>
                      {n.important && <Badge text="Važno" variant="warning" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Slike */}
            <div className="py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/picture.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Slike <span className="text-xs text-slate-400 font-normal">({totalFiles})</span></h3>
                  <p className="text-xs text-slate-500">Dodane slike kontakta</p>
                </div>
              </div>
              {totalFiles === 0 ? (
                <p className="text-sm text-slate-400">Nema dodanih slika</p>
              ) : (
                <div className="space-y-2">
                  {pictures.map((pic, i) =>
                    pic.files.map((file) => {
                      const ext = file.name?.split(".").pop()?.toUpperCase() ?? "";
                      const sizeKb = Math.round(file.size / 1024);
                      const sizeLabel = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;
                      return (
                        <div
                          key={file.id || i}
                          className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-sm bg-white"
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-sm overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                            {file.url ? (
                              <img src={file.url} alt={pic.name} className="w-full h-full object-cover" />
                            ) : (
                              <ReactSVG src="/media/icons/file.svg" className="w-5 h-5 stroke-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-700 truncate">{file.name || pic.name}</div>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              {ext && <span>{ext}</span>}
                              {ext && sizeKb > 0 && <span>·</span>}
                              {sizeKb > 0 && <span>{sizeLabel}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ContactSummaryStep);
